import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Printer, Save, ArrowRight } from "lucide-react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useDirty, useDirtyGuard, useUnsavedChangesWarning } from "../context/DirtyContext";

/** Two voucher kinds — the route param maps directly to the backend kind. */
const VOUCHER_KINDS = {
    customer: "voucher_customer",  // سند صرف عملاء
    shop: "voucher_shop",          // سند صرف محل
};

const PURITY_OPTIONS = ["24", "22", "21", "18"];

function emptyVoucherData() {
    return {
        voucher_number: "",
        voucher_date: new Date().toISOString().slice(0, 10),
        paid_to: "",
        amount: "",
        gold_purity: "",
        being: "",
        payer_name: "",
        receiver_name: "",
        ro_exchange: "",   // R.O. text (top-right of the slip)
        ml_code: "",       // ML code (top-left)
        gold_code: "",     // G code (top-left)
    };
}

export default function Voucher() {
    const { type } = useParams();
    const navigate = useNavigate();
    const { t, lang } = useLang();
    const { company } = useAuth();
    const { guardedRun } = useDirty();
    const isRTL = lang === "ar";

    const kind = VOUCHER_KINDS[type];
    const isCustomer = kind === "voucher_customer";
    const headerLabel = isCustomer ? t.voucherCustomerKicker : t.voucherShopKicker;
    const fallbackKicker = lang === "ar" ? "سند صرف" : "Payment Voucher";

    const [data, setData] = useState(() => {
        const d = emptyVoucherData();
        d.payer_name = company?.company_name || "";
        return d;
    });
    const [previewNo, setPreviewNo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedBaseline, setSavedBaseline] = useState(() => {
        const d = emptyVoucherData();
        d.payer_name = company?.company_name || "";
        return JSON.stringify(d);
    });

    const isDirty = JSON.stringify(data) !== savedBaseline;

    useUnsavedChangesWarning(isDirty);

    useEffect(() => {
        if (!kind) return;
        (async () => {
            try {
                const r = await api.get(`/ledgers/voucher/next-number?kind=${kind}`);
                setPreviewNo(r.data?.next ?? null);
            } catch {
                setPreviewNo(null);
            }
        })();
    }, [kind]);

    if (!kind) {
        return (
            <AppShell>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-700">
                    {t.voucherInvalidType || "نوع السند غير معروف"}
                </div>
            </AppShell>
        );
    }

    const update = (patch) => {
        setData((d) => ({ ...d, ...patch }));
    };

    const amt = parseFloat(data.amount);
    const isFilled = (
        data.paid_to.trim() &&
        isFinite(amt) && amt > 0 &&
        data.being.trim() &&
        data.payer_name.trim() &&
        data.receiver_name.trim() &&
        data.voucher_date
    );

    const onSave = async () => {
        if (!isFilled) {
            toast.error(t.voucherFillAllRequired || "أكمل جميع الحقول المطلوبة");
            return;
        }
        setSaving(true);
        try {
            const body = {
                kind,
                client_name: data.paid_to.trim() || "—",
                voucher_date: data.voucher_date,
                voucher_data: {
                    voucher_date: data.voucher_date,
                    paid_to: data.paid_to.trim(),
                    amount: parseFloat(data.amount),
                    gold_purity: data.gold_purity || "",
                    being: data.being.trim(),
                    payer_name: data.payer_name.trim(),
                    receiver_name: data.receiver_name.trim(),
                    ro_exchange: data.ro_exchange.trim(),
                    ml_code: data.ml_code.trim(),
                    gold_code: data.gold_code.trim(),
                    company_name: company?.company_name || "",
                },
                save_status: "final",
            };
            const r = await api.post("/ledgers", body);
            setSavedBaseline(JSON.stringify(data));
            toast.success(t.voucherSavedToast || `تم حفظ السند رقم ${r.data?.voucher_number || ""}`);
            navigate(`/history/${kind}/${r.data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onPrint = () => {
        // Mark the document with the voucher class so the print stylesheet
        // hides the chrome and keeps only the slip area.
        document.body.classList.add("printing-voucher");
        window.print();
        setTimeout(() => document.body.classList.remove("printing-voucher"), 300);
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-3xl space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 no-print">
                    <div>
                        <button
                            type="button"
                            onClick={() => guardedRun(() => navigate("/"))}
                            className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                            data-testid="voucher-back-btn"
                        >
                            <ArrowRight className="h-4 w-4" />
                            {t.backToLedgers || "العودة للدفاتر"}
                        </button>
                        <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                            {headerLabel || fallbackKicker}
                        </p>
                        <h1 className="font-heading text-2xl font-bold text-neutral-900 sm:text-3xl">
                            {t.voucherNewTitle || "إنشاء سند صرف جديد"}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onPrint}
                            data-testid="voucher-print-btn"
                            className="h-9 gap-2"
                        >
                            <Printer className="h-4 w-4" />
                            {t.print || "طباعة"}
                        </Button>
                        <Button
                            type="button"
                            onClick={onSave}
                            disabled={saving || !isFilled}
                            className="h-9 gap-2 bg-amber-700 text-white hover:bg-amber-800"
                            data-testid="voucher-save-btn"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {t.save || "حفظ"}
                        </Button>
                    </div>
                </div>

                <VoucherSlip
                    isRTL={isRTL}
                    data={data}
                    update={update}
                    previewNo={previewNo}
                    companyName={company?.company_name || ""}
                    voucherTypeLabel={isCustomer
                        ? (t.voucherCustomerLabel || "سند صرف عملاء")
                        : (t.voucherShopLabel || "سند صرف محل")}
                    readOnly={false}
                />
            </div>
        </AppShell>
    );
}

/** Visual slip that mirrors the printed booklet — used both for creation and
 *  the read-only view inside HistoryDetail. */
export function VoucherSlip({ isRTL, data, update, previewNo, companyName, voucherTypeLabel, readOnly }) {
    const onField = (key) => (e) => update && update({ [key]: e.target.value });
    return (
        <div
            className="voucher-slip rounded-2xl border-2 border-neutral-900 bg-white p-5 sm:p-7 text-neutral-900 shadow-lg print:rounded-none print:border-0 print:shadow-none"
            dir={isRTL ? "rtl" : "ltr"}
            data-testid="voucher-slip"
        >
            {/* TOP STRIP — company name (large) + voucher type + R.O. + No. */}
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3 border-b-2 border-neutral-900 pb-3">
                <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
                        R.O. <span className="font-mono">{readOnly ? (data.ro_exchange || "—") : null}</span>
                    </div>
                    {readOnly ? null : (
                        <Input
                            value={data.ro_exchange}
                            onChange={onField("ro_exchange")}
                            placeholder="R.O."
                            className="mt-1 h-8 w-28 text-sm"
                            data-testid="voucher-ro-input"
                        />
                    )}
                </div>
                <div className="flex-1 text-center">
                    <div className="font-heading text-2xl font-extrabold leading-tight text-neutral-900 sm:text-3xl">
                        {companyName || "—"}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
                        PAYMENT VOUCHER — سند صرف
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-bold text-amber-900" data-testid="voucher-type-label">
                        {voucherTypeLabel}
                    </div>
                </div>
                <div className="text-left">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">No.</div>
                    <div className="font-mono text-2xl font-bold text-rose-600" data-testid="voucher-number">
                        {data.voucher_number
                            ? String(data.voucher_number).padStart(4, "0")
                            : (previewNo ? String(previewNo).padStart(4, "0") : "----")}
                    </div>
                </div>
            </div>

            {/* CODE STRIP — ML / G */}
            <div className="mb-4 grid grid-cols-2 gap-3 sm:max-w-md">
                <Field
                    label="ML مشتری"
                    value={data.ml_code}
                    onChange={onField("ml_code")}
                    readOnly={readOnly}
                    testid="voucher-ml-code"
                />
                <Field
                    label="G مبيع"
                    value={data.gold_code}
                    onChange={onField("gold_code")}
                    readOnly={readOnly}
                    testid="voucher-gold-code"
                />
            </div>

            {/* Date */}
            <Field
                label="التاريخ — Date"
                value={data.voucher_date}
                type="date"
                onChange={onField("voucher_date")}
                readOnly={readOnly}
                required
                testid="voucher-date"
            />

            {/* Paid to */}
            <Field
                label="المدفوع إلى السيد / السادة — Paid to Mr./M/S"
                value={data.paid_to}
                onChange={onField("paid_to")}
                readOnly={readOnly}
                required
                testid="voucher-paid-to"
            />

            {/* Amount */}
            <Field
                label="مبلغ وقدره — The Sum of Rials Omani"
                value={data.amount}
                onChange={onField("amount")}
                type="number"
                step="0.001"
                readOnly={readOnly}
                required
                testid="voucher-amount"
            />

            {/* Gold Purity radios */}
            <div className="mt-3" data-testid="voucher-purity-group">
                <div className="mb-1 text-[12px] font-semibold text-neutral-700">
                    نسبة عيار الذهب — Gold Purity
                </div>
                <div className="flex flex-wrap gap-2">
                    {PURITY_OPTIONS.map((p) => {
                        const selected = data.gold_purity === p;
                        return (
                            <button
                                key={p}
                                type="button"
                                disabled={readOnly}
                                onClick={() => update && update({ gold_purity: selected ? "" : p })}
                                className={`rounded-lg border px-3 py-1.5 text-sm font-bold transition ${
                                    selected
                                        ? "border-amber-700 bg-amber-700 text-white"
                                        : "border-neutral-300 bg-white text-neutral-700 hover:border-amber-500"
                                }`}
                                data-testid={`voucher-purity-${p}`}
                            >
                                {p}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Being */}
            <div className="mt-3" data-testid="voucher-being-wrap">
                <div className="mb-1 text-[12px] font-semibold text-neutral-700">
                    وذلك عن — Being
                </div>
                {readOnly ? (
                    <div className="min-h-[64px] rounded-md border border-neutral-300 bg-neutral-50 p-2 text-sm whitespace-pre-wrap">
                        {data.being || "—"}
                    </div>
                ) : (
                    <Textarea
                        value={data.being}
                        onChange={onField("being")}
                        rows={3}
                        className="text-sm"
                        data-testid="voucher-being"
                    />
                )}
            </div>

            {/* Names — replaces signatures */}
            <div className="mt-5 grid grid-cols-1 gap-4 border-t-2 border-dashed border-neutral-300 pt-4 sm:grid-cols-2">
                <Field
                    label="اسم المسلم — Name"
                    value={data.payer_name}
                    onChange={onField("payer_name")}
                    readOnly={readOnly}
                    required
                    testid="voucher-payer-name"
                />
                <Field
                    label="اسم المستلم — Receiver's Name"
                    value={data.receiver_name}
                    onChange={onField("receiver_name")}
                    readOnly={readOnly}
                    required
                    testid="voucher-receiver-name"
                />
            </div>
        </div>
    );
}

function Field({ label, value, onChange, readOnly, required, type, step, testid }) {
    return (
        <div className="mt-2">
            <div className="mb-1 text-[12px] font-semibold text-neutral-700">
                {label} {required && !readOnly ? <span className="text-rose-600">*</span> : null}
            </div>
            {readOnly ? (
                <div className="min-h-[36px] rounded-md border border-neutral-300 bg-neutral-50 px-2 py-1.5 text-sm">
                    {value || "—"}
                </div>
            ) : (
                <Input
                    value={value}
                    onChange={onChange}
                    type={type || "text"}
                    step={step}
                    className="h-9 text-sm"
                    data-testid={testid}
                />
            )}
        </div>
    );
}
